import React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface Point {
  t: string;
  v: number;
}

function tickLabel(iso: string, windowKey: string): string {
  const d = new Date(iso);
  if (windowKey === '7d') {
    return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' });
  }
  return d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
}

export const HealthAreaChart: React.FC<{
  points: Point[];
  unit: string;
  color?: string;
  windowKey: string;
  emptyLabel?: string;
}> = ({
  points,
  unit,
  color = '#2D8CCA',
  windowKey,
  emptyLabel = 'No samples in this window yet',
}) => {
  if (!points.length) {
    return (
      <div className="flex h-64 items-center justify-center rounded-xl border border-dashed border-[var(--border)] bg-[var(--muted)]/40 text-sm text-[var(--muted-foreground)]">
        {emptyLabel}
      </div>
    );
  }

  const data = points.map((p) => ({
    t: p.t,
    v: Number(p.v),
    label: tickLabel(p.t, windowKey),
  }));

  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="healthAreaFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.22} />
              <stop offset="100%" stopColor={color} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: '#64748b' }}
            tickLine={false}
            axisLine={{ stroke: '#e2e8f0' }}
            minTickGap={24}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#64748b' }}
            tickLine={false}
            axisLine={false}
            width={48}
            tickFormatter={(v: number) =>
              unit === 'percent' ? `${Math.round(v)}` : Number(v).toLocaleString('en-ZA', { maximumFractionDigits: 1 })
            }
          />
          <Tooltip
            contentStyle={{
              borderRadius: 12,
              border: '1px solid #e2e8f0',
              fontFamily: 'Montserrat, sans-serif',
              fontSize: 12,
            }}
            formatter={(value: number) => [
              unit === 'percent'
                ? `${Number(value).toFixed(1)}%`
                : unit === 'ms'
                  ? `${Math.round(value)} ms`
                  : Number(value).toLocaleString('en-ZA', { maximumFractionDigits: 2 }),
              '',
            ]}
            labelFormatter={(_, payload) => payload?.[0]?.payload?.t
              ? new Date(payload[0].payload.t).toLocaleString('en-ZA')
              : ''}
          />
          <Area
            type="monotone"
            dataKey="v"
            stroke={color}
            strokeWidth={2}
            fill="url(#healthAreaFill)"
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};
